import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { translations, resolveKey, interpolate } from "@/i18n/translations";

const STORAGE_KEY = "audioflip_lang";
const DEFAULT_LANG = "es";
const AVAILABLE = ["es", "en"];
const OG_LOCALE_MAP = { es: "es_ES", en: "en_US" };

const LanguageContext = createContext(null);

function detectInitial() {
  try {
    // 1. URL query param has highest priority (for SEO alternate links).
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("lang");
      if (q && AVAILABLE.includes(q)) return q;
    }
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && AVAILABLE.includes(saved)) return saved;
    const nav = (navigator.language || "es").slice(0, 2).toLowerCase();
    if (AVAILABLE.includes(nav)) return nav;
  } catch {
    /* SSR / private mode */
  }
  return DEFAULT_LANG;
}

/**
 * Rewrite the static hreflang/canonical/og:locale tags in index.html so their
 * hrefs point to the real deployment origin. Also update the current-page
 * ?lang= query param so bookmarks/share-links carry the language.
 */
function syncSeoTags(lang) {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const origin = window.location.origin;
  const path = window.location.pathname || "/";

  const setAlt = (code, url) => {
    const el = document.querySelector(`link[data-i18n-alt="${code}"]`);
    if (el) el.setAttribute("href", url);
  };
  setAlt("es", `${origin}${path}?lang=es`);
  setAlt("en", `${origin}${path}?lang=en`);
  setAlt("x-default", `${origin}${path}`);

  const canonical = document.querySelector("link[data-i18n-canonical]");
  if (canonical) canonical.setAttribute("href", `${origin}${path}?lang=${lang}`);

  const og = document.querySelector("meta[data-i18n-og-locale]");
  if (og) og.setAttribute("content", OG_LOCALE_MAP[lang] || OG_LOCALE_MAP[DEFAULT_LANG]);
  const ogAlt = document.querySelector("meta[data-i18n-og-locale-alt]");
  if (ogAlt) {
    const otherLang = lang === "es" ? "en" : "es";
    ogAlt.setAttribute("content", OG_LOCALE_MAP[otherLang]);
  }

  // Update URL ?lang= without adding a history entry.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("lang", lang);
    window.history.replaceState(null, "", url.toString());
  } catch {
    /* ignore */
  }
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const initial = detectInitial();
    // Set axios default synchronously so the VERY FIRST request from any
    // child (e.g. QuotaBar's /api/quota on mount) already carries the
    // Accept-Language header. Without this, React's bottom-up effect order
    // would run child effects (and their axios calls) BEFORE our own useEffect.
    try {
      axios.defaults.headers.common["Accept-Language"] = initial;
    } catch {
      /* ignore */
    }
    return initial;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      if (typeof document !== "undefined") {
        document.documentElement.lang = lang;
        const dict = translations[lang] || translations[DEFAULT_LANG];
        // Dynamic <title> and description for SEO + share previews.
        if (dict.seo?.title) document.title = dict.seo.title;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc && dict.seo?.description) {
          metaDesc.setAttribute("content", dict.seo.description);
        }
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle && dict.seo?.ogTitle) {
          ogTitle.setAttribute("content", dict.seo.ogTitle);
        }
        const ogDesc = document.querySelector('meta[property="og:description"]');
        if (ogDesc && dict.seo?.description) {
          ogDesc.setAttribute("content", dict.seo.description);
        }
      }
      // Tell the backend which language we want error messages in.
      axios.defaults.headers.common["Accept-Language"] = lang;
      syncSeoTags(lang);
    } catch {
      /* ignore */
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (AVAILABLE.includes(next)) setLangState(next);
  }, []);

  const toggleLang = useCallback(() => {
    setLangState((prev) => (prev === "es" ? "en" : "es"));
  }, []);

  const t = useCallback(
    (key, params) => {
      const dict = translations[lang] || translations[DEFAULT_LANG];
      return interpolate(resolveKey(dict, key), params);
    },
    [lang]
  );

  const value = { lang, setLang, toggleLang, t, available: AVAILABLE };
  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return ctx;
}
